import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { TextArea } from '../../Shared/Component/forms/TextArea';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { CandidateProfile, initialCandidateProfile } from '../Types/CandidateType';
import { getCandidateById, updateCandidate } from '../Services/CandidateService';
import { DropdownMulti } from '../../Shared/Component/forms/DropdownMulti';
import { getAllArchitectureExperience, getAllBusinessImpactType, getAllCoreExpertise, getAllDecisionSpeed, getAllDomainExpertise, getAllHiringAuthority, getAllOwnerShipLevel, getAllProductScale, getAllReputationSignals, getAllRoleLevel, getAllStartupRiskPreference, getAllTargetRole, getAllTechnologyStack, getAllWorkFlexibility, getAllWorkStyle } from '../../Admin/Services/AdminService';
import { mapToOptions } from '../../Shared/Component/forms/dropdownHelpers';
import { Option, ownerShipLevelResponse, targetRoleResponse } from '../../Admin/Types/AdminType';
import GenericListManager from '../../Shared/Component/forms/GenericListManager';
import { DropdownSingle } from '../../Shared/Component/forms/DropdownSingle';
import { storage } from '../../Shared/Utils/authStorage';
import AccountAccessPanel from '../../Shared/Component/AccountAccessPanel';
import { WorkHistoryManager } from '../../Shared/Component/forms/WorkHistoryManager';
import { EducationManager } from '../../Shared/Component/forms/EducationManager';

/**
 * Utility to join class names conditionally
 */
function classNames(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Interface for the InputField component props
 */
interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  placeholder?: string;
  className?: string;
}

/** Small section heading used to break the long forms into scannable blocks. */
const SectionTitle: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="mb-1">
    <h3 className="text-sm font-black uppercase tracking-widest text-indigo-200/90">{title}</h3>
    {hint && <p className="mt-1 text-xs font-semibold text-slate-500">{hint}</p>}
  </div>
);

/** Read-only pill for account/verification metadata, so it stops looking like an editable input. */
const StatChip: React.FC<{ label: string; value?: string | null; tone?: 'ok' | 'off' | 'neutral' }> = ({
  label,
  value,
  tone = 'neutral',
}) => {
  const toneClass =
    tone === 'ok'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
      : tone === 'off'
      ? 'border-slate-700 bg-slate-800/40 text-slate-500'
      : 'border-slate-700 bg-slate-900/60 text-slate-200';

  return (
    <div className={`rounded-xl border px-4 py-3 ${toneClass}`}>
      <p className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
      <p className="mt-1 truncate text-sm font-bold">{value || '—'}</p>
    </div>
  );
};

/** Converts the backend score (Double) into a 0-100 number for the progress visuals. */
const toScore = (value?: string | number | null): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, Math.round(parsed))) : 0;
};

const CandidateEditProfileComponent: React.FC = () => {
  const categories: string[] = ['Personal', 'Professional Overview', 'Experience & Skills', 'Leadership & Executive', 'Scores / AI Insights'];

 const {
    values,
    setAllValues,
    handleInputChange,
    handleRequiredValueChange,
    handleValueChange,
    handleRequiredValueChangeForDD,
  } = useFormController<CandidateProfile>(initialCandidateProfile);
  
  const [candidate, setCandidate] = useState<CandidateProfile | null>(initialCandidateProfile);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
      
  
  useEffect(() => {
    const fetchCandidate = async () => {
      // 1. Get the ID inside the effect to ensure fresh data
      const userId = storage.getUserId();
    
      // 2. Guard Clause: Don't fetch if there is no ID
      if (!userId) {
        setError("No user ID found. Please log in again.");
        setLoading(false);
        return;
      }
  
      try {
        setLoading(true); // Ensure loading is true when starting
        const response = await getCandidateById(userId);
        
        if (response.success) {
          setCandidate(response.data);
          setAllValues(response.data as CandidateProfile);
        } else {
          setError(response.message || "Could not retrieve candidate profile.");
        }
      } catch (err) {
        setError("An unexpected error occurred while loading the profile.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCandidate();
  }, []); // Empty dependency array is fine if using localStorage



const [roleOptions, setRoleOptions] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllTargetRole().then(response => {
    if (response?.data) {
      // We use "targetRole" (the string) for both parts
      const formatted = mapToOptions(response.data, "targetRole", "targetRole");
      setRoleOptions(formatted); 
    }
  });
}, []);

  const [currentRole, setCuurentRole] = useState<Option<string>[]>([]);
  useEffect(() => {
    getAllTargetRole().then(response => {
      if (response?.data) {
        setCuurentRole(mapToOptions(response.data, "targetRole", "targetRole")); 
      }
    });
  }, []);


const [ownerShipLevel, setOwnerShipLevel] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllOwnerShipLevel().then(response => {
    if (response?.data) {
      // We use "ownershipLevel" (the string) for both parts
      const formatted = mapToOptions(response.data, "ownerShipLevel", "ownerShipLevel");
      setOwnerShipLevel(formatted); 
    }
  });
}, []);
    



const [hiringAuthority, setHiringAuthority] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllHiringAuthority().then(response => {
    if (response?.data) {
      // We use "hiringAuthority" (the string) for both parts
      const formatted = mapToOptions(response.data, "hiringAuthority", "hiringAuthority");
      setHiringAuthority(formatted); 
    }
  });
}, []);


const [productScale, setProductScale] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllProductScale().then(response => {
    if (response?.data) {
      // We use "productScale" (the string) for both parts
      const formatted = mapToOptions(response.data, "productScale", "productScale");
      setProductScale(formatted); 
    }
  });
}, []);


const [technologyStack, setTechnologyStack] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllTechnologyStack().then(response => {
    if (response?.data) {
      // We use "productScale" (the string) for both parts
      const formatted = mapToOptions(response.data, "technologyStack", "technologyStack");
      setTechnologyStack(formatted); 
    }
  });
}, []);


const [coreExpertise, setCoreExpertise] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllCoreExpertise().then(response => {
    if (response?.data) {
      // We use "productScale" (the string) for both parts
      const formatted = mapToOptions(response.data, "coreExpertise", "coreExpertise");
      setCoreExpertise(formatted); 
    }
  });
}, []);

const [domainExpertise, setDomainExpertise] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllDomainExpertise().then(response => {
    if (response?.data) {
      // We use "productScale" (the string) for both parts
      const formatted = mapToOptions(response.data, "domainExpertise", "domainExpertise");
      setDomainExpertise(formatted); 
    }
  });
}, []);









const [architectureExperience, setArchitectureExperience] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllArchitectureExperience().then(response => {
    if (response?.data) {
      // We use "architectureExperience" (the string) for both parts
      const formatted = mapToOptions(response.data, "architectureExperience", "architectureExperience");
      setArchitectureExperience(formatted); 
    }
  });
}, []);


const [businessImpactType, setBusinessImpactType] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllBusinessImpactType().then(response => {
    if (response?.data) {
      // We use "businessImpactType" (the string) for both parts
      const formatted = mapToOptions(response.data, "businessImpactType", "businessImpactType");
      setBusinessImpactType(formatted); 
    }
  });
}, []);

const [workStyle, setWorkStyle] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllWorkStyle().then(response => {
    if (response?.data) {
      // We use "workStyle" (the string) for both parts
      const formatted = mapToOptions(response.data, "workStyle", "workStyle");
      setWorkStyle(formatted); 
    }
  });
}, []);


const [workFlexibility, setWorkFlexibility] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllWorkFlexibility().then(response => {
    if (response?.data) {
      // We use "workFlexibility" (the string) for both parts
      const formatted = mapToOptions(response.data, "workFlexibility", "workFlexibility");
      setWorkFlexibility(formatted); 
    }
  });
}, []);

const availabilityStatusOptions = [
  { label: "Actively exploring", value: "ACTIVELY_EXPLORING" },
  { label: "Open to selective offers", value: "OPEN_TO_SELECTIVE_OFFERS" },
  { label: "Not actively looking", value: "NOT_ACTIVELY_LOOKING" }
];


const interviewReadiness = [
 { label: "Ready immediately", value: "READY_IMMEDIATELY" },
  { label: "Need 1–2 weeks preparation", value: "NEED_1_2_WEEKS_PREPARATION" },
  { label: "Exploring only", value: "EXPLORING_ONLY" }
];

const [decisionSpeed, setDecisionSpeed] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllDecisionSpeed().then(response => {
    if (response?.data) {
      // We use "decisionSpeed" (the string) for both parts
      const formatted = mapToOptions(response.data, "decisionSpeed", "decisionSpeed");
      setDecisionSpeed(formatted); 
    } 
  });
}, []);

const [startupRiskPreference, setStartupRiskPreference] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllStartupRiskPreference().then(response => {
    if (response?.data) {
      // We use "startupRiskPreference" (the string) for both parts
      const formatted = mapToOptions(response.data, "startupRiskPreference", "startupRiskPreference");
      setStartupRiskPreference(formatted); 
    } 
  });
}, []);



const [reputationSignals, setReputationSignals] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllReputationSignals().then(response => {
    if (response?.data) {
      // We use "reputationSignals" (the string) for both parts
      const formatted = mapToOptions(response.data, "reputationSignals", "reputationSignals");
      setReputationSignals(formatted); 
    } 
  });
}, []);

const [roleLevel, setRoleLevel] = useState<Option<string>[]>([]);
useEffect(() => {
  getAllRoleLevel().then(response => {
    if (response?.data) {
      // We use "roleLevel" (the string) for both parts
      const formatted = mapToOptions(response.data, "roleLevel", "roleLevel");
      setRoleLevel(formatted); 
    } 
  });
}, []);



  const handleSubmit = async () => {
  setAlert(null);
  const userId = storage.getUserId();

  if (saving) return;
  setSaving(true);

  try {
    const response = await updateCandidate(values, userId as string);

    if (response.success) {
      setCandidate(values);
      setAlert({
        variant: "success",
        title: "Profile Updated",
        message: response.message || "Your changes have been saved and your scores recalculated.",
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: "error",
        title: "Update Failed",
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  } catch {
    setAlert({
      variant: "error",
      title: "Update Failed",
      message: "Something went wrong while saving. Please try again.",
      autoCloseMs: 5000,
      onClose: () => setAlert(null),
    });
  } finally {
    setSaving(false);
  }
  }

  // if (!candidate) {
  //   return (
  //     <div className="flex items-center justify-center p-10 text-slate-500">
  //       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-3"></div>
  //       Loading profile data...
  //     </div>
  //   );
  // }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 text-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        <p className="text-sm font-bold tracking-wide text-slate-400">Loading your profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 px-4 text-center text-slate-100">
        <AlertCircle className="h-9 w-9 text-rose-400" />
        <p className="text-lg font-black">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-xl bg-cyan-500 px-5 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400"
        >
          Try again
        </button>
      </div>
    );
  }

  const completion = toScore(candidate?.profileCompleteScore);
  const overall = toScore(values.overallScore);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 py-6 px-4 sm:px-8 lg:px-16 pb-32 text-slate-100 antialiased">
      <div className="max-w-7xl mx-auto w-full">
  
        {/* Animated Alert Container */}
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 w-auto sm:w-full sm:max-w-md animate-in fade-in slide-in-from-top-4">
          {alert && (
            <Alert
              variant={alert.variant} 
              title={alert.title} 
              message={alert.message} 
              onClose={() => setAlert(null)} 
            />
          )}
        </div>
    
        {/* Header */}
        <header className="mb-8 flex flex-col gap-6 rounded-2xl border border-white/10 bg-white/5 p-6 shadow-xl backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
          <div>
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-3xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-amber-400 sm:text-4xl"
            >
              Edit Profile
            </motion.h1>
            <p className="mt-1 text-sm font-medium text-slate-400">
              A complete profile gets seen first by hiring partners.
            </p>
          </div>

          {/* Completion ring */}
          <div className="flex items-center gap-4 self-center sm:self-auto">
            <div className="relative h-16 w-16 shrink-0">
              <svg viewBox="0 0 100 100" className="h-16 w-16 -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="10" className="text-white/10" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="url(#completionGradient)"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={2 * Math.PI * 42 * (1 - completion / 100)}
                  className="transition-[stroke-dashoffset] duration-700 ease-out"
                />
                <defs>
                  <linearGradient id="completionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="50%" stopColor="#a855f7" />
                    <stop offset="100%" stopColor="#f59e0b" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-black leading-none text-white">{completion}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">%</span>
              </div>
            </div>
            <div className="text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Profile Strength</p>
              <p className="text-sm font-black text-white">
                {completion >= 90
                  ? 'Excellent — recruiter ready'
                  : completion >= 70
                  ? 'Good — a few gaps left'
                  : completion >= 40
                  ? 'Fair — keep going'
                  : 'Just started'}
              </p>
              <p className="mt-0.5 text-[11px] font-semibold text-slate-500">Overall score {overall}/100</p>
            </div>
          </div>
        </header>
  
        <Tab.Group>
          {/* Tab List - Mobile Responsive Overlap Fix */}
          <Tab.List className="flex flex-nowrap space-x-2 rounded-xl bg-slate-950/60 backdrop-blur-md p-1.5 shadow-2xl border border-white/5 overflow-x-auto scrollbar-none snap-x snap-mandatory">
            {categories.map((category) => (
              <Tab
                key={category}
                className={({ selected }) =>
                  classNames(
                    'flex-shrink-0 w-auto min-w-[130px] sm:flex-1 snap-center rounded-lg py-3 px-4 text-xs sm:text-sm font-bold transition-all duration-300 focus:outline-none uppercase tracking-wider text-center',
                    selected
                      ? 'bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20 border-b-2 border-cyan-400'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  )
                }
              >
                {category}
              </Tab>
            ))}
          </Tab.List>
          
          <Tab.Panels className="mt-6">
  
            {/* Panel 1: Personal */}
            <Tab.Panel
              as={motion.div}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8 rounded-2xl border border-white/10 bg-slate-900/80 p-5 shadow-2xl backdrop-blur-xl sm:p-8"
            >
              <section className="space-y-1">
                <SectionTitle
                  title="Identity & Contact"
                  hint="This is how hiring partners and your invite chain reach you."
                />
                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <TextInput label="First Name" name="firstName" value={values?.firstName} onChange={handleInputChange("firstName")} />
                  <TextInput label="Last Name" name="lastName" value={values?.lastName} onChange={handleInputChange("lastName")} />
                  <TextInput label="Current City" name="city" value={values?.city as string} onChange={handleInputChange("city")} />
                  <TextInput label="Mobile Number" name="phoneNumber" value={values?.phoneNumber as string} onChange={handleInputChange("phoneNumber")} />
                  <TextInput label="LinkedIn URL" name="linkedInUrl" value={values?.linkedInUrl as string} onChange={handleInputChange("linkedInUrl")} />
                  <InputField label="User Email" name="userEmail" type="text" readOnly value={values.userEmail} onChange={handleInputChange("userEmail")} />
                </div>
              </section>

              <section className="space-y-1 border-t border-white/5 pt-8">
                <SectionTitle
                  title="Trust & Verification"
                  hint="Managed by our team. Nothing here is editable."
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <StatChip label="User Role" value={values.userRole} />
                  <StatChip label="User Status" value={values.userStatus} />
                  <StatChip
                    label="Account Verified"
                    value={values.isUserVerified ? 'Verified' : 'Not Verified'}
                    tone={values.isUserVerified ? 'ok' : 'off'}
                  />
                  <StatChip
                    label="Profile Status"
                    value={values.profileComplete ? 'Complete' : 'Incomplete'}
                    tone={values.profileComplete ? 'ok' : 'off'}
                  />
                  <StatChip
                    label="LinkedIn"
                    value={values.linkedinVerified ? 'Verified' : 'Not Verified'}
                    tone={values.linkedinVerified ? 'ok' : 'off'}
                  />
                  <StatChip
                    label="Work Email"
                    value={values.workEmailVerified ? 'Verified' : 'Not Verified'}
                    tone={values.workEmailVerified ? 'ok' : 'off'}
                  />
                  <StatChip
                    label="Phone"
                    value={values.phoneVerified ? 'Verified' : 'Not Verified'}
                    tone={values.phoneVerified ? 'ok' : 'off'}
                  />
                  <StatChip
                    label="Profile"
                    value={values.profileVerified ? 'Verified' : 'Not Verified'}
                    tone={values.profileVerified ? 'ok' : 'off'}
                  />
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <StatChip
                    label="Created At"
                    value={
                      values.createdAt
                        ? new Date(values.createdAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                        : undefined
                    }
                  />
                  <StatChip
                    label="Last Updated"
                    value={
                      values.updatedAt
                        ? new Date(values.updatedAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                        : undefined
                    }
                  />
                </div>
              </section>
            </Tab.Panel>
  
            {/* Panel 2: Professional */}
            <Tab.Panel
              as={motion.div}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/10 bg-slate-900/80 p-5 shadow-2xl backdrop-blur-xl sm:p-8"
            >
              <div className="space-y-8">
                <section className="space-y-1">
                  <SectionTitle
                    title="Your Pitch"
                    hint="The first thing a hiring partner reads. Lead with your scope and years."
                  />
                  <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="md:col-span-2 lg:col-span-3">
                      <span className="ml-1 text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200">Professional Headline</span>
                      <TextArea placeholder="e.g. Expert with 7+ years of experience. Specialized in Scalable Web Systems..." name='professionalHeadline' value={values?.professionalHeadline as string} onChange={handleInputChange("professionalHeadline")} />
                      <p className="mt-1.5 ml-1 text-[11px] font-semibold text-slate-500">
                        {(values?.professionalHeadline?.length ?? 0)} characters — aim for one punchy line.
                      </p>
                    </div>

                    <div className="md:col-span-2 lg:col-span-3">
                      <span className="ml-1 text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200">About You (Professional)</span>
                      <TextArea label="" name="professionalPitch" placeholder="Professional Pitch" value={values?.professionalPitch || ""} onChange={handleInputChange("professionalPitch")}/>
                    </div>

                    <div className="md:col-span-2 lg:col-span-3">
                      <DropdownMulti label="Public reputation or recognition signals" maxItems={3} options={reputationSignals} value={values.reputationSignals} onChange={handleRequiredValueChangeForDD("reputationSignals")} />
                    </div>
                  </div>
                </section>

                <section className="space-y-1 border-t border-white/5 pt-8">
                  <SectionTitle title="Current Role" hint="Where you are now and how long you have been there." />
                  <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <DropdownSingle label="Current Role Title" options={currentRole} value={values.currentRole} onChange={(val) => handleValueChange("currentRole")(val ?? "")} />
                    <InputField label="Current Company" name="currentCompany" placeholder="Motherson Digital System" value={values?.currentCompany} onChange={handleInputChange("currentCompany")}/>
                    <InputField label="Total Experience (Years)" name="totalYearsOfExperience" placeholder="Experience" value={values?.totalYearsOfExperience || ""} onChange={handleInputChange("totalYearsOfExperience")}/>
                  </div>
                </section>

                <section className="space-y-1 border-t border-white/5 pt-8">
                  <SectionTitle title="Search Preferences" hint="Helps us match you to the right leadership mandates." />
                  <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <DropdownSingle label="Ready for interviews?" options={interviewReadiness} value={values.interviewReadiness} onChange={(val) => handleValueChange("interviewReadiness")(val ?? "")} />
                    <DropdownSingle label="Remote Work / Relocation flexibility" options={workFlexibility} value={values.workFlexibility} onChange={(val) => handleValueChange("workFlexibility")(val ?? "")} />
                    <DropdownSingle label="Openness to new opportunities" options={availabilityStatusOptions} value={values.availabilityStatus} onChange={(val) => handleValueChange("availabilityStatus")(val ?? "")} />
                    <DropdownSingle label="Decision speed for strong offers" options={decisionSpeed} value={values.decisionSpeed} onChange={(val) => handleValueChange("decisionSpeed")(val ?? "")} />
                  </div>
                </section>

                <section className="space-y-1 border-t border-white/5 pt-8">
                  <SectionTitle title="Compensation & Availability" hint="Only shared with hiring partners you engage with." />
                  <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <TextInput label="Expected Compensation (Lakhs)" name="expectedCompensation" placeholder='25 lakhs' value={values?.expectedCompensation?.toString() ?? ""} onChange={handleInputChange("expectedCompensation")} />
                    <TextInput label="Minimum Acceptable Compensation (Lakhs)" name="minimumAcceptableCompensation" placeholder='25 lakhs' value={values?.minimumAcceptableCompensation?.toString() ?? ""} onChange={handleInputChange("minimumAcceptableCompensation")} />
                    <TextInput label="Notice Period (Days)" name="noticePeriodDays" placeholder='30 days' value={values?.noticePeriodDays?.toString() ?? ""} onChange={handleInputChange("noticePeriodDays")} />
                  </div>
                </section>
              </div>
            </Tab.Panel>
  
            {/* Panel 3: Experience */}
            <Tab.Panel className="bg-slate-900/80 backdrop-blur-xl p-5 sm:p-8 rounded-2xl shadow-2xl border border-white/10">
              <div className="space-y-6">
                
                {/* Section 1: Roles & Leadership */}
                <div className="bg-gradient-to-br from-indigo-950/40 via-slate-900/40 to-slate-900/20 p-5 sm:p-6 rounded-xl border border-indigo-500/20 shadow-sm space-y-4">
                  <h2 className="text-lg font-bold text-white/90 flex items-center gap-2 tracking-tight mb-2">
                    <span className="h-2 w-2 rounded-full bg-indigo-400 animate-pulse" />
                    Roles & Leadership
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DropdownMulti label="Level of ownership in last role" maxItems={3} options={ownerShipLevel} value={values.ownerShipLevel} onChange={handleRequiredValueChangeForDD("ownerShipLevel")} />
                    <DropdownSingle label="Current professional level" options={roleLevel} value={values.roleLevel} onChange={(val) => handleValueChange("roleLevel")(val ?? "")} />
                    <InputField label="Team Size Handled"  placeholder="Handled Team Size" name="teamSizeLed" type="text" value={values.teamSizeLed ?? "0"} onChange={handleInputChange("teamSizeLed")} />
                    <DropdownMulti label="Involved in hiring decisions?" maxItems={3} options={hiringAuthority} value={values.hiringAuthority} onChange={handleRequiredValueChangeForDD("hiringAuthority")} />
                    <DropdownMulti label="Best fit work environment" maxItems={3} options={workStyle} value={values.workStyle} onChange={handleRequiredValueChangeForDD("workStyle")} />
                    <DropdownMulti label="Comfortable joining startups?" maxItems={3} options={startupRiskPreference} value={values.startupRiskPreference} onChange={handleRequiredValueChangeForDD("startupRiskPreference")} />
                    <div className="md:col-span-2">
                      <DropdownMulti label="Targeting next roles" maxItems={3} options={roleOptions} value={values.targetRole} onChange={handleRequiredValueChangeForDD("targetRole")} />
                    </div>
                  </div>
                </div>
  
                {/* Section 2: Technical & Product Depth */}
                <div className="bg-gradient-to-br from-purple-950/40 via-slate-900/40 to-slate-900/20 p-5 sm:p-6 rounded-xl border border-purple-500/20 shadow-sm space-y-4">
                  <h2 className="text-lg font-bold text-white/90 flex items-center gap-2 tracking-tight mb-2">
                    <span className="h-2 w-2 rounded-full bg-purple-400 animate-pulse" />
                    Technical / Product Depth
                  </h2>
                  <div className="space-y-4">
                    <DropdownMulti label="Designed / worked architectures" maxItems={3} options={architectureExperience} value={values.architectureExperience} onChange={handleRequiredValueChangeForDD("architectureExperience")} />
                    <DropdownMulti label="Product / system scales handled" maxItems={3} options={productScale} value={values.productScale} onChange={handleRequiredValueChangeForDD("productScale")} />
                    <DropdownMulti label="Strongest technical areas" maxItems={20} options={technologyStack} value={values.technologyStack} onChange={handleRequiredValueChangeForDD("technologyStack")} />
                    <DropdownMulti label="Strongest core expertises" maxItems={10} options={coreExpertise} value={values.coreExpertise} onChange={handleRequiredValueChangeForDD("coreExpertise")} />
                    <DropdownMulti label="Industry domain expertises" maxItems={10} options={domainExpertise} value={values.domainExpertise} onChange={handleRequiredValueChangeForDD("domainExpertise")} />
                  </div>
                </div>
  
                {/* Section 3: Business Impact */}
                <div className="bg-gradient-to-br from-emerald-950/40 via-slate-900/40 to-slate-900/20 p-5 sm:p-6 rounded-xl border border-emerald-500/20 shadow-sm space-y-4">
                  <h2 className="text-lg font-bold text-white/90 flex items-center gap-2 tracking-tight mb-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    Business Impact
                  </h2>
                  <div className="space-y-4">
                    <DropdownMulti label="Types of business impact created" maxItems={5} options={businessImpactType} value={values.businessImpactType} onChange={handleRequiredValueChangeForDD("businessImpactType")} />
                    
                    <GenericListManager<string>
                      candidate={values}
                      setCandidate={setAllValues}
                      dataKey="businessImpactDescription"
                      title="Describe Business Impact with metrics (e.g. Increased engagement by 30%)"
                      maxItems={5}
                      colorClass="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-colors rounded-xl font-bold"
                      getLabel={(item) => item}
                      getId={(item) => item}
                      createItem={(input) => input}
                    />
  
                    <GenericListManager<string>
                      candidate={values}
                      setCandidate={setAllValues}
                      dataKey="complexProblemSolved"
                      title="Describe Complex Problems You've Solved..."
                      maxItems={5}
                      colorClass="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 transition-colors rounded-xl font-bold"
                      getLabel={(item) => item}
                      getId={(item) => item}
                      createItem={(input) => input}
                    />
                  </div>
                </div>
  
                {/* Section 4: Achievements */}
                <div className="bg-gradient-to-br from-amber-950/40 via-slate-900/40 to-slate-900/20 p-5 sm:p-6 rounded-xl border border-amber-500/20 shadow-sm space-y-4">
                  <h2 className="text-lg font-bold text-white/90 flex items-center gap-2 tracking-tight mb-2">
                    <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                    Achievements
                  </h2>
                  <div className="space-y-4">
                    <GenericListManager<string>
                      candidate={values}
                      setCandidate={setAllValues}
                      dataKey="topAchievements"
                      title="List Top 5 Professional Achievements"
                      maxItems={5}
                      colorClass="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 transition-colors rounded-xl font-bold"
                      getLabel={(item) => item}
                      getId={(item) => item}
                      createItem={(input) => input}
                    />
  
                    <GenericListManager<string>
                      candidate={values}
                      setCandidate={setAllValues}
                      dataKey="notableCompanies"
                      title="List top companies you worked with"
                      maxItems={3}
                      colorClass="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 transition-colors rounded-xl font-bold"
                      getLabel={(item) => item}
                      getId={(item) => item}
                      createItem={(input) => input}
                    />
                  </div>
                </div>
  
              </div>
            </Tab.Panel>

            {/* Panel 4: Leadership & Executive */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 rounded-2xl border border-white/10 bg-slate-900/80 p-5 shadow-2xl backdrop-blur-xl sm:p-8">
              <section className="space-y-1">
                <SectionTitle title="Leadership Scope" hint="The metrics executive recruiters filter on first." />
                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <InputField label="Years in Leadership" name="yearsInLeadership" type="number" placeholder="5" value={values.yearsInLeadership ?? ''} onChange={handleInputChange('yearsInLeadership')} />
                  <InputField label="Reports To" name="reportsToLevel" placeholder="e.g. CTO / CEO / Board" value={values.reportsToLevel} onChange={handleInputChange('reportsToLevel')} />
                  <InputField label="Annual Budget Managed (Lakhs)" name="budgetManagedLakhs" type="number" placeholder="50" value={values.budgetManagedLakhs ?? ''} onChange={handleInputChange('budgetManagedLakhs')} />
                  <InputField label="Revenue Scope (Crores)" name="revenueScopeCrores" type="number" placeholder="10" value={values.revenueScopeCrores ?? ''} onChange={handleInputChange('revenueScopeCrores')} />
                  <InputField label="Travel Willingness (%)" name="travelWillingnessPercent" type="number" placeholder="25" value={values.travelWillingnessPercent ?? ''} onChange={handleInputChange('travelWillingnessPercent')} />
                  <InputField label="Highest Education" name="highestEducation" placeholder="e.g. MBA, M.Tech" value={values.highestEducation} onChange={handleInputChange('highestEducation')} />

                  <div className="md:col-span-2 lg:col-span-3 flex items-center gap-3 px-1">
                    <input
                      id="boardOrInvestorExposure"
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-600 bg-slate-950 text-cyan-500 focus:ring-cyan-500"
                      checked={values.boardOrInvestorExposure}
                      onChange={(e) => handleValueChange('boardOrInvestorExposure')(e.target.checked)}
                    />
                    <label htmlFor="boardOrInvestorExposure" className="text-sm font-bold text-slate-300">
                      I have regular board / investor exposure
                    </label>
                  </div>
                </div>
              </section>

              <section className="space-y-1 border-t border-white/5 pt-8">
                <SectionTitle title="Scope & Credentials" hint="Multi-select tags that recruiters filter by." />
                <div className="space-y-5">
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="functionScope"
                    title="Functions Led (e.g. Engineering, Product, Data)"
                    maxItems={5}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="geographicScope"
                    title="Geographic Scope (e.g. APAC, North America, Global)"
                    maxItems={5}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="companyStageExperience"
                    title="Company Stage Experience (e.g. Seed, Series A, Enterprise)"
                    maxItems={5}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="transformationExperience"
                    title="Transformation Experience (e.g. M&A, Turnaround, Migration)"
                    maxItems={5}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="executiveEducation"
                    title="Executive Education (e.g. MBA, INSEAD, ISB)"
                    maxItems={5}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                  <GenericListManager<string>
                    candidate={values}
                    setCandidate={setAllValues}
                    dataKey="languages"
                    title="Languages"
                    maxItems={10}
                    colorClass="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 transition-colors rounded-xl font-bold"
                    getLabel={(item) => item}
                    getId={(item) => item}
                    createItem={(input) => input}
                  />
                </div>
              </section>

              <section className="space-y-1 border-t border-white/5 pt-8">
                <SectionTitle title="Career Timeline" hint="Work history and education add credibility for senior mandates." />
                <div className="space-y-6">
                  <WorkHistoryManager items={values.workHistory} onChange={(items) => setAllValues({ ...values, workHistory: items })} />
                  <EducationManager items={values.education} onChange={(items) => setAllValues({ ...values, education: items })} />
                </div>
              </section>
            </Tab.Panel>

            {/* Panel 5: Scores / AI Insights */}
            <Tab.Panel as={motion.div} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 p-5 shadow-2xl sm:p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-indigo-400">
                  AI Insights & Performance Scores
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-400 sm:text-sm">
                  Recalculated every time you save your profile.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
                {[
                  { label: 'Leadership Score', icon: '👑', value: values.leadershipScore, from: 'from-amber-400', to: 'to-amber-600' },
                  { label: 'Architecture Score', icon: '💻', value: values.architectureScore, from: 'from-cyan-400', to: 'to-blue-600' },
                  { label: 'Business Impact Score', icon: '📈', value: values.businessImpactScore, from: 'from-emerald-400', to: 'to-teal-600' },
                  { label: 'Achievement Score', icon: '🏆', value: values.achievementScore, from: 'from-fuchsia-400', to: 'to-purple-600' },
                ].map((score) => {
                  const numeric = toScore(score.value);
                  return (
                    <div
                      key={score.label}
                      className="rounded-xl border border-white/5 bg-white/5 p-5 backdrop-blur-sm transition-colors hover:border-indigo-500/30"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-black uppercase tracking-widest text-slate-300">
                          <span className="mr-1.5">{score.icon}</span>
                          {score.label}
                        </p>
                        <span className="text-2xl font-black text-white">
                          {numeric}
                          <span className="text-xs font-bold text-slate-500">/100</span>
                        </span>
                      </div>
                      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${score.from} ${score.to} transition-all duration-700 ease-out`}
                          style={{ width: `${numeric}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 rounded-xl border-2 border-cyan-500/20 bg-gradient-to-r from-cyan-950/50 to-indigo-950/50 p-5 shadow-lg shadow-cyan-950/50 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300">🚀 Overall Score</p>
                    <p className="mt-1 text-xs font-semibold text-slate-400">
                      Your composite leadership score across all dimensions.
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <span className="text-4xl font-black text-white">{overall}</span>
                    <span className="text-sm font-bold text-slate-500">/100</span>
                  </div>
                </div>
                <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 transition-all duration-700 ease-out"
                    style={{ width: `${overall}%` }}
                  />
                </div>
              </div>
            </Tab.Panel>
            
          </Tab.Panels>
        </Tab.Group>

        {/* Account access */}
        <section className="mt-8 rounded-2xl border border-rose-500/25 bg-rose-950/20 p-5 shadow-2xl backdrop-blur-xl sm:p-8">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
              <Trash2 size={17} />
            </span>
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest text-rose-300">
                Account Access
              </h2>
              <p className="mt-1 text-xs font-semibold text-slate-500">
                Switch your account off, or ask us to delete it.
              </p>
            </div>
          </div>

          <p className="mt-4 text-sm text-slate-400">
            Deactivating hides you from the platform and blocks sign-in until you reactivate —
            nothing is deleted. Permanent deletion removes your candidate profile, scores,
            saved filters and sign-in credentials, and is only done once an administrator
            reviews your request.
          </p>

          <AccountAccessPanel className="mt-4" />
        </section>
      </div>
  
      {/* Floating Footer Action Strip */}
      <div className="fixed bottom-0 left-0 right-0 z-[999] border-t border-white/10 bg-slate-950/80 p-4 shadow-[0_-12px_24px_rgba(0,0,0,0.5)] backdrop-blur-lg">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <p className="hidden text-xs font-semibold text-slate-500 sm:block">
            Scores recalculate automatically when you save.
          </p>
          <motion.button 
            onClick={handleSubmit}
            disabled={saving}
            whileHover={{ scale: 1.01, translateY: -1 }}
            whileTap={{ scale: 0.99 }}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 py-3.5 text-sm font-bold tracking-wide text-white shadow-xl shadow-blue-900/30 transition-all hover:shadow-purple-900/40 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-12 sm:text-base"
          >
            {saving ? (
              <>
                <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin" />
                <span>Saving…</span>
              </>
            ) : (
              <>
                <span>Save Profile Changes</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </div>
  );
};

/**
 * Reusable Typed Input Component
 */
// const InputField: React.FC<InputFieldProps> = ({ label, className = "", ...props }) => (
//   <div className={className}>
//     <label className="block text-sm font-bold uppercase text-slate-200/70 mb-2 ml-1 mt-7">{label}</label>
//     <input 
//       {...props} 
//       className="w-full px-5 py-3 rounded-xl border-2 border-indigo-50 bg-white focus:bg-white focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-gray-300 shadow-sm disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-100" 
//     />
//   </div>
// );

const InputField: React.FC<InputFieldProps> = ({
  label,
  placeholder,
  className = "",
  ...props
}) => (
  <div className={className}>
    <label className="block text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200 mb-2 ml-1 mt-7">
      {label}
    </label>

    <div className="group relative">
      {/* Same focus glow as the shared TextInput so every field on the page matches */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-xl opacity-20 group-focus-within:opacity-100 transition duration-300 blur-[2px]" />

      <input
        {...props}
        placeholder={placeholder}
        className="relative w-full rounded-xl bg-white dark:bg-zinc-900 px-4 py-2.5
                   text-zinc-900 dark:text-zinc-100
                   placeholder:text-zinc-400
                   border border-zinc-200 dark:border-zinc-700
                   focus:border-transparent focus:outline-none
                   transition-all duration-200
                   read-only:bg-zinc-50 dark:read-only:bg-zinc-800/30 read-only:cursor-default"
      />
    </div>
  </div>
);

export default CandidateEditProfileComponent;